import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { BookOpen, Heart, Users, Flower2, ArrowRight } from "lucide-react";

const VIDEO_URL_1 = "https://customer-assets.emergentagent.com/job_e1e47677-df71-49c4-917c-4fa0a875df63/artifacts/mw2zin4a_855867-hd_1920_1080_30fps.mp4";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_e1e47677-df71-49c4-917c-4fa0a875df63/artifacts/mtdbnete_Untitled%20design%20-%202026-01-07T124500.690.png";

const services = [
  {
    title: "Advance Care Planning",
    description: "Guidance in completing important end-of-life documents including Advance Directive, Healthcare Power of Attorney, and Living Will. I help you clarify your values and wishes, ensuring your voice is heard.",
    icon: BookOpen,
    link: "/advance-care-planning",
  },
  {
    title: "End-of-Life Coaching and Care Coordination",
    description: "Compassionate emotional support and advocacy across healthcare settings, ensuring your voice and values remain central throughout your journey.",
    icon: Heart,
    link: "/end-of-life-coaching",
  },
  {
    title: "Legacy & Life Review",
    description: "Create meaningful keepsakes through memory books, letters, and guided life reflections for your loved ones. Preserve your story and wisdom for future generations.",
    icon: Users,
    link: "/legacy-projects",
  },
  {
    title: "Vigil & Funeral Planning",
    description: "Support in honoring final wishes with intention, including symptom comfort, environment rituals, and funeral planning. Creating peaceful final moments.",
    icon: Flower2,
    link: "/vigil-and-funeral-planning",
  },
];

const Services = () => {
  // Generate Services Schema
  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": services.map((service, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Service",
        "name": service.title,
        "description": service.description,
        "provider": {
          "@type": "LocalBusiness",
          "name": "Serenity Passage"
        },
        "areaServed": {
          "@type": "Place",
          "name": "Columbia, MO and Mid-Missouri"
        }
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>End-of-Life Doula Services Columbia, MO | Serenity Passage | Mid-Missouri</title>
        <meta
          name="description"
          content="End-of-life doula services in Columbia, Missouri including advance care planning, hospice support, legacy projects, and vigil planning. Compassionate non-medical support in Mid-Missouri."
        />
        <meta
          name="keywords"
          content="end-of-life doula services Columbia MO, death doula Columbia Missouri, advance care planning Mid-Missouri, legacy projects, vigil planning, hospice support"
        />
        <link rel="canonical" href="https://www.serenitypassage.net/services" />
        <script type="application/ld+json">
          {JSON.stringify(servicesSchema)}
        </script>
      </Helmet>

      {/* Hero Banner with Video */}
      <section className="relative py-24 overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={VIDEO_URL_1} type="video/mp4" />
        </video>
        {/* White Overlay */}
        <div className="absolute inset-0 bg-white/70"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center pt-4">
          {/* Large Logo at top */}
          <img
            src={LOGO_URL}
            alt="Serenity Passage - End-of-Life Doula Services in Columbia, MO"
            className="h-56 sm:h-64 md:h-72 lg:h-80 w-auto object-contain mb-6"
          />
          <h1 className="text-4xl sm:text-5xl font-serif text-gray-800 text-center mb-4">
            Compassionate Services
          </h1>
          <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto">
            Supporting you through every aspect of end-of-life planning and care with dignity, comfort, and peace
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="group bg-rose-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                data-testid={`service-link-${index}`}
              >
                <service.icon className="w-12 h-12 text-rose-600 mb-4 group-hover:scale-110 transition-transform" />
                <h2 className="text-2xl font-semibold text-gray-800 mb-3">
                  {service.title}
                </h2>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {service.description}
                </p>
                <span className="inline-flex items-center gap-2 text-rose-600 font-medium group-hover:gap-3 transition-all">
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-white to-rose-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif text-gray-800 mb-4">
            Ready to Take the Next Step?
          </h2>
          <p className="text-gray-600 mb-8">
            Your first 30-minute consultation is complimentary. I'm here to listen and support you.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-8 py-4 rounded-full font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
          >
            Schedule a Consultation
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default Services;
